import { useCallback, useReducer } from 'react';

import type { BuildingSpecification } from 'app/modules/ifc-editor/types/buildingSpecification';

const MAX_HISTORY = 100;

interface HistoryState {
    past: BuildingSpecification[];
    present: BuildingSpecification | null;
    future: BuildingSpecification[];
}

type HistoryAction =
    | { type: 'load'; specification: BuildingSpecification }
    | { type: 'apply'; update: (current: BuildingSpecification) => BuildingSpecification; transient: boolean }
    | { type: 'checkpoint' }
    | { type: 'undo' }
    | { type: 'redo' };

const reducer = (state: HistoryState, action: HistoryAction): HistoryState => {
    switch (action.type) {
        case 'load':
            return { past: [], present: action.specification, future: [] };
        case 'checkpoint':
            return state.present
                ? { past: [...state.past, state.present].slice(-MAX_HISTORY), present: state.present, future: [] }
                : state;
        case 'apply': {
            if (!state.present) {
                return state;
            }
            const next = action.update(state.present);
            if (next === state.present) {
                return state;
            }
            return action.transient
                ? { ...state, present: next }
                : { past: [...state.past, state.present].slice(-MAX_HISTORY), present: next, future: [] };
        }
        case 'undo':
            return state.past.length === 0 || !state.present
                ? state
                : {
                      past: state.past.slice(0, -1),
                      present: state.past[state.past.length - 1],
                      future: [state.present, ...state.future]
                  };
        case 'redo':
            return state.future.length === 0 || !state.present
                ? state
                : { past: [...state.past, state.present], present: state.future[0], future: state.future.slice(1) };
        default:
            return state;
    }
};

/**
 * Undoable editor state. Drags call `checkpoint` once and then apply `transient` updates,
 * so a whole drag is a single undo step.
 */
export const useSpecificationHistory = () => {
    const [state, dispatch] = useReducer(reducer, { past: [], present: null, future: [] });

    return {
        specification: state.present,
        canUndo: state.past.length > 0,
        canRedo: state.future.length > 0,
        load: useCallback((specification: BuildingSpecification) => dispatch({ type: 'load', specification }), []),
        apply: useCallback(
            (update: (current: BuildingSpecification) => BuildingSpecification, transient = false) =>
                dispatch({ type: 'apply', update, transient }),
            []
        ),
        checkpoint: useCallback(() => dispatch({ type: 'checkpoint' }), []),
        undo: useCallback(() => dispatch({ type: 'undo' }), []),
        redo: useCallback(() => dispatch({ type: 'redo' }), [])
    };
};
